"use client";

import { cn } from "@/core/lib/utils";
import { Button } from "@/core/components/ui/button";
import { useTranslation } from "react-i18next";
import { BrunaAvatar } from "@/core/components/bruna/bruna-avatar";
import { AGENT_THEMES } from "../constants/agents";
import type { AgentShortcut, AgentType } from "../types";

type AgentHubPickerProps = {
  onShortcutClick: (agentType: AgentType, shortcut: AgentShortcut) => void;
};

// Experiência incluída: dono da action "motivos_de_uso_insights" (PED-9109).
const AGENT_ORDER: AgentType[] = ["operation", "estrategy", "experience"];

export function AgentHubPicker({ onShortcutClick }: AgentHubPickerProps) {
  const [translate] = useTranslation(["core"]);

  // Identidade única (PED-14256): o cabeçalho é sempre a Bruna. Desde o PED-14564
  // os modos não aparecem mais como "Bruna · <modo>": as sugestões de todos os
  // modos vêm numa lista só, e cada uma continua entregando o seu modo
  // (agentType) ao handler — é ele que define o forced_agent_type do envio.
  const shortcuts = AGENT_ORDER.flatMap((agentType) =>
    AGENT_THEMES[agentType].shortcuts.map((shortcut) => ({
      agentType,
      shortcut,
    })),
  );

  return (
    <div className="flex flex-col items-center px-4">
      <div className="flex flex-col items-center w-full">
        <BrunaAvatar size={96} className="mb-3" />

        <h2 className="text-[24px] text-darkgray-900 mb-2 font-bold">
          {translate("chatbot-bruna-name") as string}
        </h2>

        <p className="px-2 mb-6 text-[16px] text-center text-darkgray-600">
          {translate("chatbot-hub-intro")}
        </p>
      </div>

      <div
        className="flex w-full flex-col gap-2"
        data-testid="agent-hub-shortcuts"
      >
        {shortcuts.map(({ agentType, shortcut }) => (
          <Button
            key={`${agentType}:${shortcut.action}`}
            variant="outline"
            className={cn(
              "justify-start w-full h-auto px-4 py-3 rounded-2xl",
              "text-[16px] font-semibold text-left whitespace-normal",
              "bg-white border-lightgray-300 text-darkgray-500",
              "hover:bg-lightgray-200",
            )}
            onClick={() => onShortcutClick(agentType, shortcut)}
          >
            <span className="flex flex-wrap gap-2 items-center">
              {translate(shortcut.textKey)}
              {shortcut.badgeKey && (
                <span className="rounded-full bg-primary-500 px-2 py-0.5 text-[12px] font-semibold text-darkgray-800">
                  {translate(shortcut.badgeKey)}
                </span>
              )}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}
